// LeadStatusChangeSheet — mobile bottom sheet that lets a broker move a
// lead between pipeline stages by tapping a row (instead of
// drag-and-drop, which feels bad on touch, or the older inline Select
// dropdown, which the design no longer uses on lead cards).
//
// Tracks design source:
//   `/Users/datnguyen/workspace/playground/laed detail.html` — second
//   reference image showing the row-list status picker (Mới /
//   Đã liên hệ / Hẹn xem · HIỆN TẠI / Đàm phán / Chốt deal /
//   Thất bại · Cần lý do).
//
// All status changes route through the existing LeadStatusGuardProvider
// so the "VIEWING_SCHEDULED requires an appointment" rule stays
// uniformly enforced with the kanban drag handler and the desktop
// LeadStatusSelect dropdown.

import { ChevronRight, Clock, X } from "lucide-react";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  LEAD_STATUSES,
  LEAD_STATUS_DESCRIPTIONS,
  LEAD_STATUS_DOT_COLORS,
  LEAD_STATUS_LABELS,
} from "@/features/lead/constants";
import { useLeadStatusGuard } from "@/features/lead/components/lead-status-guard-context";
import type { Lead, LeadStatus } from "@/features/lead/types";

interface LeadStatusChangeSheetProps {
  lead: Lead;
  open: boolean;
  onClose: () => void;
}

// Avatar palette mirrors the other lead surfaces (kanban card, detail
// hero). Duplicated here intentionally — keeping each surface's
// avatar self-contained avoids a tangle of cross-imports for what
// amounts to a 7-row lookup table.
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
  "bg-cyan-100 text-cyan-700",
  "bg-fuchsia-100 text-fuchsia-700",
  "bg-pink-100 text-pink-700",
] as const;

function colorForId(id: number): string {
  return AVATAR_COLORS[Math.abs(id) % AVATAR_COLORS.length]!;
}

function initialsFor(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter((s) => s.length > 0);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return (first + last).toUpperCase() || "?";
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  const diffMs = Date.now() - then;
  return Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

export function LeadStatusChangeSheet({
  lead,
  open,
  onClose,
}: LeadStatusChangeSheetProps) {
  const guard = useLeadStatusGuard();
  const paddedId = String(lead.id).padStart(4, "0");
  const days = daysSince(lead.createdAt);
  const lastUpdate = formatDateShort(lead.updatedAt);

  const handlePick = (next: LeadStatus) => {
    if (next === lead.status) {
      onClose();
      return;
    }
    // Close first, then dispatch. The guard may open its own modal
    // (VIEWING_SCHEDULED with no appointment), and having both surfaces
    // on screen at once would feel chaotic on a phone.
    onClose();
    guard.requestStatusChange(lead, next);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[88vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <SheetTitle className="sr-only">Đổi trạng thái lead</SheetTitle>

        {/* Header — avatar + eyebrow + name + close button. */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <div
            aria-hidden="true"
            className={cn(
              "flex size-12 shrink-0 items-center justify-center rounded-2xl text-[14px] font-bold",
              colorForId(lead.customer.id),
            )}
          >
            {initialsFor(lead.customer.fullName)}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="truncate font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
              LEAD-{paddedId} · Đổi trạng thái
            </p>
            <p className="truncate text-[16px] font-bold leading-tight">
              {lead.customer.fullName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>

        {/* Body — status rows. Padding below the rows leaves room for
            the footer's clock-readout block. */}
        <div className="flex flex-col gap-2 px-4 pt-3 pb-2">
          {LEAD_STATUSES.map((status) => (
            <StatusRow
              key={status}
              status={status}
              isCurrent={status === lead.status}
              disabled={guard.isPending}
              onPick={() => handlePick(status)}
            />
          ))}
        </div>

        {/* Footer — pipeline-age + last-update readout, plus a one-line
            hint clarifying that this surface only changes the status
            (it doesn't navigate to the detail page). The hint is in
            the design and worth keeping: brokers tap rows expecting
            navigation otherwise. */}
        <div className="border-t border-border px-4 py-3">
          <p className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
            <Clock aria-hidden="true" className="size-3.5 shrink-0" />
            <span>
              Trong pipeline: <b className="text-foreground">{days} ngày</b>
              <span aria-hidden="true"> · </span>
              gần nhất: {lastUpdate}
            </span>
          </p>
          <p className="mt-2 text-center font-mono text-[11px] text-ink-faint">
            Chỉ thay đổi trạng thái — không mở chi tiết.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface StatusRowProps {
  status: LeadStatus;
  isCurrent: boolean;
  disabled: boolean;
  onPick: () => void;
}

// Soft-tone tint applied to the current row. Mirrors the kanban pill
// palette so the same hue threads from kanban → card pill → this sheet.
const ROW_CURRENT_TONE: Record<LeadStatus, string> = {
  NEW: "border-slate-300 bg-slate-50",
  CONTACTED: "border-indigo-200 bg-indigo-50",
  VIEWING_SCHEDULED: "border-amber-300 bg-amber-50",
  NEGOTIATING: "border-indigo-300 bg-indigo-100/60",
  WON: "border-emerald-200 bg-emerald-50",
  LOST: "border-rose-200 bg-rose-50",
};

function StatusRow({ status, isCurrent, disabled, onPick }: StatusRowProps) {
  const isLost = status === "LOST";
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-current={isCurrent ? "true" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors disabled:opacity-50",
        isCurrent
          ? ROW_CURRENT_TONE[status]
          : "border-border bg-card hover:bg-muted",
      )}
    >
      {/* Radio circle. Filled when current, outline otherwise. We use
          the status's saturated dot colour for the inner fill so the
          left edge of the row carries the hue. */}
      <span
        aria-hidden="true"
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border-2",
          isCurrent ? "border-foreground" : "border-border",
        )}
      >
        {isCurrent ? (
          <span
            className={cn("size-2 rounded-full", LEAD_STATUS_DOT_COLORS[status])}
          />
        ) : null}
      </span>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn(
              "size-2 shrink-0 rounded-full",
              LEAD_STATUS_DOT_COLORS[status],
            )}
          />
          <span className="text-[14px] font-bold leading-tight">
            {LEAD_STATUS_LABELS[status]}
          </span>
          {isCurrent ? (
            <span className="inline-flex items-center rounded-md border border-foreground/30 px-1.5 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-foreground uppercase">
              Hiện tại
            </span>
          ) : null}
          {isLost && !isCurrent ? (
            // "Cần lý do" — design hints brokers can't drop a lead
            // into LOST silently. Today the lostReason capture is on
            // the detail page; we surface the requirement here so it
            // doesn't feel like a missing field.
            <span className="ml-auto inline-flex items-center rounded-md border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">
              Cần lý do
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
          {LEAD_STATUS_DESCRIPTIONS[status]}
        </p>
      </div>

      {/* Chevron on the right — affordance hint that the row "goes
          somewhere" (i.e. actually commits the change). Hidden on the
          current row because tapping it just closes the sheet. */}
      {!isCurrent ? (
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground"
        />
      ) : null}
    </button>
  );
}
